import React, { useState, useEffect, useRef } from "react";
import { API_ENDPOINTS } from "../config/constants";
import VehicleLink from "./VehicleLink";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faTag,
  faChevronDown,
  faChevronUp,
  faCar,
} from "@fortawesome/free-solid-svg-icons";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AVAILABLE_TAGS } from "../constants/tagConstants";
import SearchBar from "./SearchBar";
import { getVehicleMakes } from "../services/vehicleMakes";
import AlphabetFilter from "./ExplorePageComponents/AlphabetFilter";
import TagsFilter from "./ExplorePageComponents/TagsFilter";
import VehicleFilter from "./ExplorePageComponents/VehicleFilter";
import useStatePreservation from "../hooks/useStatePreservation";
import useVehicleData from "../hooks/useVehicleData";

// Constants
const TOP_TAGS = AVAILABLE_TAGS.slice(0, 4); // Show first 4 tags by default
const ALPHABET_ROW1 = Array.from("ABCDEFGHIJKLM");
const ALPHABET_ROW2 = Array.from("NOPQRSTUVWXYZ");

// Storage keys
const EXPLORE_STATE_STORAGE_KEY = "explorePageState";
const NAVIGATION_TRACKING_KEY = "explorePageNavigation";
const LAST_VISITED_PATH_KEY = "lastVisitedPath";
const STATE_RESTORED_TIMESTAMP_KEY = "exploreStateRestoredAt";

const ExplorePage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const queryParams = new URLSearchParams(location.search);
  const tagFromUrl = queryParams.get("tag");
  const searchFromUrl = queryParams.get("search");
  const startsWithFromUrl = queryParams.get("startsWith");
  const makeFromUrl = queryParams.get("make");
  const modelFromUrl = queryParams.get("model");
  const resultsRef = useRef(null);
  const { t } = useTranslation();

  // State variables
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState("recent");
  const [selectedTag, setSelectedTag] = useState(tagFromUrl || "");
  const [showAllTags, setShowAllTags] = useState(false);
  const [vehicleMakes, setVehicleMakes] = useState([]);
  const [loadingMakes, setLoadingMakes] = useState(true);
  const [selectedMake, setSelectedMake] = useState(makeFromUrl || "");
  const [selectedModel, setSelectedModel] = useState(modelFromUrl || "");
  const [models, setModels] = useState([]);
  const [scrollPosition, setScrollPosition] = useState(0);

  // Custom hooks
  const {
    statePreserved,
    setStatePreserved,
    stateRestoredRef,
    mountCountRef,
    highestLoadedPageRef,
    forceNewFetchRef,
    savePageState,
    loadPageState,
    forceSyncState,
    clearSavedState,
    setNavigatingToVehicle,
    checkReturningFromVehicle,
    resetStateForNewFilters,
  } = useStatePreservation(
    [], // vehicles will be added from the data hook
    page,
    true, // hasMore initial value
    sort,
    selectedTag,
    selectedMake,
    selectedModel,
    location
  );

  const {
    vehicles,
    setVehicles,
    loading,
    setLoading,
    error,
    setError,
    hasMore,
    setHasMore,
    fetchVehicles,
  } = useVehicleData(
    page,
    sort,
    selectedTag,
    searchFromUrl,
    startsWithFromUrl,
    selectedMake,
    selectedModel,
    stateRestoredRef,
    statePreserved,
    highestLoadedPageRef,
    forceNewFetchRef,
    location,
    navigate,
    setNavigatingToVehicle
  );

  // Handle for vehicle link clicks - pass this to VehicleLink
  const handleVehicleLinkClick = (e) => {
    console.log("Vehicle link clicked");
    setNavigatingToVehicle();
  };

  // Helper to preserve existing params when updating URL
  const preserveExistingParams = (params) => {
    // Preserve tag parameter if it exists
    if (selectedTag) {
      params.set("tag", selectedTag);
    }

    // Preserve startsWith parameter if it exists
    const startsWith = params.get("startsWith");
    if (startsWith) {
      params.set("startsWith", startsWith);
    }

    // Preserve search and singleDigitMode if they exist
    const search = params.get("search");
    const singleDigitMode = params.get("singleDigitMode");
    if (search) {
      params.set("search", search);
      if (singleDigitMode === "true") {
        params.set("singleDigitMode", "true");
      }
    }
  };

  // Cleanup function to make sure we clear navigation flag if component unmounts
  // except when navigating to a vehicle page
  useEffect(() => {
    // Store initial mount timestamp
    const mountTimestamp = Date.now();

    return () => {
      // Only run this logic after component has been mounted for a minimum time
      // to avoid clearing flags during quick remounts
      const unmountTimestamp = Date.now();
      const componentLifetime = unmountTimestamp - mountTimestamp;

      console.log("ExplorePage unmounting", {
        componentLifetime,
        currentPath: window.location.pathname,
      });

      // If component was mounted for less than 500ms, it's likely a router transition
      // or if we're navigating to a vehicle page path, we should keep the flag
      const isNavigatingToVehiclePath =
        window.location.pathname.startsWith("/plate/");

      if (componentLifetime < 500 || isNavigatingToVehiclePath) {
        console.log("Quick unmount or navigating to vehicle, preserving flags");
      } else {
        // If we're not navigating to a vehicle page but the component is unmounting normally,
        // clear the navigation flags
        sessionStorage.removeItem(NAVIGATION_TRACKING_KEY);
        localStorage.removeItem(NAVIGATION_TRACKING_KEY);
        console.log("Cleared navigation tracking on normal unmount");
      }
    };
  }, []);

  // Detect remount and force sync state if needed
  useEffect(() => {
    // If this is not the first mount and we've already restored state
    if (mountCountRef.current > 1 && stateRestoredRef.current) {
      console.log(
        "Detected remount after state restoration, forcing state sync"
      );
      const savedState = forceSyncState();
      if (savedState) {
        setVehicles(savedState.vehicles || []);
        setPage(savedState.page || 1);
        highestLoadedPageRef.current = savedState.page || 1;
        setHasMore(savedState.hasMore || false);
        setSort(savedState.sort || "recent");
        setSelectedTag(savedState.selectedTag || "");
        setSelectedMake(savedState.selectedMake || "");
        setSelectedModel(savedState.selectedModel || "");

        // Try scroll restoration multiple times to ensure it works
        const targetScrollPos = savedState.scrollPosition || 0;
        setTimeout(() => window.scrollTo(0, targetScrollPos), 50);
        setTimeout(() => window.scrollTo(0, targetScrollPos), 150);
        setTimeout(() => window.scrollTo(0, targetScrollPos), 300);
        setTimeout(() => window.scrollTo(0, targetScrollPos), 500);
      }
    }
  }, []);

  // Enhanced scroll tracking with debounce and additional state saving triggers
  useEffect(() => {
    let scrollDebounceTimer = null;
    let scrollSaveTimer = null;

    const handleScroll = () => {
      // Update state immediately with debounce
      if (scrollDebounceTimer) {
        clearTimeout(scrollDebounceTimer);
      }

      scrollDebounceTimer = setTimeout(() => {
        setScrollPosition(window.scrollY);
      }, 100);

      // Save state less frequently during scrolling
      if (!scrollSaveTimer) {
        scrollSaveTimer = setTimeout(() => {
          savePageState();
          scrollSaveTimer = null;
        }, 1000);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
      if (scrollDebounceTimer) clearTimeout(scrollDebounceTimer);
      if (scrollSaveTimer) clearTimeout(scrollSaveTimer);
    };
  }, [
    vehicles,
    page,
    hasMore,
    sort,
    selectedTag,
    selectedMake,
    selectedModel,
    location.search,
  ]);

  // Handle component mount and check if returning from vehicle page
  useEffect(() => {
    // Increment mount count
    mountCountRef.current += 1;

    console.log("ExplorePage mounted", {
      pathname: location.pathname,
      search: location.search,
      mountCount: mountCountRef.current,
    });

    // Check if this is a remount within a short time after state restoration
    const lastStateRestorationTime = parseInt(
      localStorage.getItem(STATE_RESTORED_TIMESTAMP_KEY) || "0",
      10
    );
    const timeSinceStateRestoration = Date.now() - lastStateRestorationTime;
    const isRecentStateRestoration = timeSinceStateRestoration < 2000; // 2 seconds threshold

    // If we've already restored state in this session or recently restored state, sync state but skip initialization
    if (stateRestoredRef.current || isRecentStateRestoration) {
      console.log("Skipping initialization - state already restored", {
        fromRef: stateRestoredRef.current,
        isRecentStateRestoration,
        timeSinceStateRestoration,
      });

      // For remounts shortly after restoration, force sync with stored state
      if (mountCountRef.current > 1) {
        const savedState = forceSyncState();
        if (savedState) {
          setVehicles(savedState.vehicles || []);
          setPage(savedState.page || 1);
          highestLoadedPageRef.current = savedState.page || 1;
          setHasMore(savedState.hasMore || false);
          setSort(savedState.sort || "recent");
          setSelectedTag(savedState.selectedTag || "");
          setSelectedMake(savedState.selectedMake || "");
          setSelectedModel(savedState.selectedModel || "");

          // Restore scroll position after a short delay to ensure rendering
          setTimeout(() => {
            window.scrollTo(0, savedState.scrollPosition || 0);
          }, 100);
        }
      }

      return;
    }

    // Check if the URL has the 'from=vehicle' parameter which indicates a back navigation
    const fromParam = queryParams.get("from");
    const isExplicitReturn = fromParam === "vehicle";

    // Check other methods for detecting return
    const isReturningFromVehicle = checkReturningFromVehicle();

    // Additional check: compare current URL with last stored explore page URL
    const lastVisitedPath = localStorage.getItem(LAST_VISITED_PATH_KEY);
    const currentPath = location.pathname + location.search;
    const isPathMatch = lastVisitedPath === currentPath;

    console.log("Navigation detection", {
      isReturningFromVehicle,
      isExplicitReturn,
      isPathMatch,
      lastVisitedPath,
      currentPath,
    });

    if (isReturningFromVehicle || isExplicitReturn) {
      // Store the current path as last visited
      localStorage.setItem(LAST_VISITED_PATH_KEY, currentPath);

      const savedState = loadPageState();

      if (savedState) {
        // Apply the saved state
        setVehicles(savedState.vehicles);
        setPage(savedState.page);
        highestLoadedPageRef.current = savedState.page; // Track the highest page we've loaded
        setHasMore(savedState.hasMore);
        setSort(savedState.sort);
        setSelectedTag(savedState.selectedTag);
        setSelectedMake(savedState.selectedMake);
        setSelectedModel(savedState.selectedModel);
        setStatePreserved(true);

        // Defer scroll restoration to ensure the DOM has been rendered
        setTimeout(() => {
          window.scrollTo(0, savedState.scrollPosition);
        }, 100);

        // Mark that we've restored state to prevent duplicate processing
        stateRestoredRef.current = true;

        // Record timestamp of state restoration
        localStorage.setItem(
          STATE_RESTORED_TIMESTAMP_KEY,
          Date.now().toString()
        );

        setLoading(false);
        console.log("State successfully restored");
      } else {
        console.log(
          "Failed to restore state, proceeding with normal initialization"
        );
        setLoading(true);
        setPage(1);
      }
    } else {
      // Normal initialization (not returning from vehicle)
      console.log("Normal initialization (not returning from vehicle)");
      clearSavedState();
      setPage(1);
      setVehicles([]);

      // Store the current path as last visited
      localStorage.setItem(LAST_VISITED_PATH_KEY, currentPath);
    }

    // Capture browser back/forward navigation with more reliable detection
    const handlePopState = (event) => {
      console.log("PopState event detected", {
        state: event.state,
        referrer: document.referrer,
      });

      // In some cases, the original navigation flags might be lost during router transitions
      // Check localStorage for persistent flags that should survive remounts
      const lastRestoredTime = parseInt(
        localStorage.getItem(STATE_RESTORED_TIMESTAMP_KEY) || "0",
        10
      );
      const timeSinceRestoration = Date.now() - lastRestoredTime;

      // If a state restoration happened very recently, don't trigger another one
      if (timeSinceRestoration < 1000) {
        console.log("Ignoring popstate soon after restoration");
        return;
      }

      // Check if we might be returning from a vehicle page
      const vehicleRegex = /^\/plate\/[A-Z0-9]+$/;
      const previousPageWasVehicle =
        document.referrer &&
        vehicleRegex.test(new URL(document.referrer).pathname);

      console.log("PopState analysis", {
        referrer: document.referrer,
        previousPageWasVehicle,
        currentPath: window.location.pathname,
      });

      if (previousPageWasVehicle) {
        console.log("Returning from vehicle via browser back button");

        // Set flags to indicate return from vehicle
        localStorage.setItem(NAVIGATION_TRACKING_KEY, "true");
        sessionStorage.setItem(NAVIGATION_TRACKING_KEY, "true");

        // Force a page reload to ensure clean state restoration
        // Add a query parameter to indicate this is a navigation return
        const url = new URL(window.location.href);
        url.searchParams.set("from", "vehicle");
        url.searchParams.set("t", Date.now()); // Add timestamp to prevent caching
        window.location.href = url.toString();
      }
    };

    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, []);

  // Keep selectedTag in sync with URL params
  useEffect(() => {
    const tagFromUrl = queryParams.get("tag") || "";
    if (selectedTag !== tagFromUrl) {
      setSelectedTag(tagFromUrl);
    }
  }, [location.search]);

  // Fetch vehicle makes on component mount
  useEffect(() => {
    const fetchMakes = async () => {
      try {
        setLoadingMakes(true);
        const makesData = await getVehicleMakes();
        setVehicleMakes(makesData);
      } catch (err) {
        console.error("Error fetching vehicle makes:", err);
      } finally {
        setLoadingMakes(false);
      }
    };

    fetchMakes();
  }, []);

  // Update models when selected make changes
  useEffect(() => {
    if (selectedMake) {
      const makeData = vehicleMakes.find((make) => make.name === selectedMake);
      setModels(
        makeData
          ? makeData.models.filter((model) => model.isActive !== false)
          : []
      );
    } else {
      setModels([]);
      setSelectedModel("");
    }
  }, [selectedMake, vehicleMakes]);

  // Keep make/model in sync with URL params
  useEffect(() => {
    const makeFromUrl = queryParams.get("make") || "";
    const modelFromUrl = queryParams.get("model") || "";

    if (selectedMake !== makeFromUrl) {
      setSelectedMake(makeFromUrl);
    }

    if (selectedModel !== modelFromUrl) {
      setSelectedModel(modelFromUrl);
    }
  }, [location.search]);

  const handleLetterClick = (letter) => {
    // Reset state for new filter
    resetStateForNewFilters();

    const params = new URLSearchParams(location.search);
    const currentStartsWith = params.get("startsWith");
    const newStartsWith = letter === "#" ? "[0-9]" : letter;

    // Clear if clicking the same letter
    if (currentStartsWith === newStartsWith) {
      params.delete("startsWith");
    } else {
      params.set("startsWith", newStartsWith);
    }

    // Keep existing tag if present
    if (selectedTag) {
      params.set("tag", selectedTag);
    }

    // Clear search-related params
    params.delete("search");
    params.delete("singleDigitMode");

    navigate(`${location.pathname}?${params.toString()}`, {
      state: { shouldScroll: true },
    });
  };

  // Prevent unnecessary data fetches after state restoration
  useEffect(() => {
    // This check ensures filters always work
    const shouldFetch =
      !stateRestoredRef.current || !statePreserved || forceNewFetchRef.current;

    // When URL params change, we should allow fetching
    if (shouldFetch) {
      console.log("Fetching vehicles due to filter/sort change", {
        stateRestoredRef: stateRestoredRef.current,
        statePreserved,
        forceNewFetch: forceNewFetchRef.current,
      });

      // Only reset page if we haven't explicitly done so already
      if (page !== 1) {
        setPage(1);
      }

      highestLoadedPageRef.current = 0; // Set to 0 so page 1 is considered "new"
      forceNewFetchRef.current = true;
      fetchVehicles(true); // Force fetch on filter change
    } else {
      console.log("Skipping fetch on filter/sort change due to restored state");
    }
  }, [
    sort,
    selectedTag,
    searchFromUrl,
    startsWithFromUrl,
    selectedMake,
    selectedModel,
  ]);

  useEffect(() => {
    // Calculate if we're loading a new page here
    const isLoadingNewPage = page > highestLoadedPageRef.current;

    console.log("Page change detected", {
      page,
      highestLoadedPage: highestLoadedPageRef.current,
      isLoadingNewPage,
    });

    // Set the force fetch flag if we're loading a new page
    if (isLoadingNewPage) {
      forceNewFetchRef.current = true;
    }

    // Don't fetch if we're preserving state and not loading a new page
    if ((!stateRestoredRef.current && !statePreserved) || isLoadingNewPage) {
      console.log(`Fetching vehicles for page ${page}`, {
        isLoadingNewPage,
        currentPage: page,
        highestLoadedPage: highestLoadedPageRef.current,
      });

      fetchVehicles(isLoadingNewPage); // Pass the loadNewPage flag directly
    } else {
      console.log(`Skipping fetch for page ${page} due to restored state`, {
        currentPage: page,
        highestLoadedPage: highestLoadedPageRef.current,
      });
    }
  }, [page]);

  useEffect(() => {
    if (location.state?.shouldScroll && resultsRef.current) {
      resultsRef.current.scrollIntoView({ behavior: "smooth" });
      navigate(location.pathname + location.search, {
        replace: true,
        state: { ...location.state, shouldScroll: false },
      });
    }
  }, [location.state]);

  const handleLoadMore = () => {
    if (!loading && hasMore) {
      console.log("Loading more vehicles", {
        currentPage: page,
        nextPage: page + 1,
        highestLoadedPage: highestLoadedPageRef.current,
      });

      // Explicitly set the force fetch flag
      forceNewFetchRef.current = true;

      // Save state before loading more
      savePageState();

      // Set the next page, this will trigger the page useEffect
      setPage((prev) => prev + 1);
    }
  };

  const handleMakeSelect = (makeName) => {
    // Reset state for new filter
    resetStateForNewFilters();

    const params = new URLSearchParams(location.search);
    const newMake = selectedMake === makeName ? "" : makeName;

    // Update URL params
    if (newMake) {
      params.set("make", newMake);
      // Clear model if make changes
      params.delete("model");
    } else {
      params.delete("make");
      params.delete("model");
    }

    // Preserve other parameters
    preserveExistingParams(params);

    // Navigate with updated params
    navigate(
      `${location.pathname}${params.toString() ? `?${params.toString()}` : ""}`,
      { replace: true }
    );
  };

  const handleModelSelect = (modelName) => {
    // Reset state for new filter
    resetStateForNewFilters();

    const params = new URLSearchParams(location.search);
    const newModel = selectedModel === modelName ? "" : modelName;

    // Update URL params
    if (newModel) {
      params.set("model", newModel);
      // Make sure make is set
      if (selectedMake) {
        params.set("make", selectedMake);
      }
    } else {
      params.delete("model");
    }

    // Preserve other parameters
    preserveExistingParams(params);

    // Navigate with updated params
    navigate(
      `${location.pathname}${params.toString() ? `?${params.toString()}` : ""}`,
      { replace: true }
    );
  };

  const handleTagSelect = (tag) => {
    // Reset state for new filter
    resetStateForNewFilters();

    const params = new URLSearchParams(location.search);
    const newTag = selectedTag === tag ? "" : tag;

    // Update URL params
    if (newTag) {
      params.set("tag", newTag);
    } else {
      params.delete("tag");
    }

    // Preserve other parameters
    if (selectedMake) {
      params.set("make", selectedMake);
      if (selectedModel) {
        params.set("model", selectedModel);
      }
    }

    // Preserve startsWith parameter if it exists
    const startsWith = params.get("startsWith");
    if (startsWith) {
      params.set("startsWith", startsWith);
    }

    // Preserve search and singleDigitMode if they exist
    const search = params.get("search");
    const singleDigitMode = params.get("singleDigitMode");
    if (search) {
      params.set("search", search);
      if (singleDigitMode === "true") {
        params.set("singleDigitMode", "true");
      }
    }

    // Navigate first, state will be updated by the useEffect
    navigate(
      `${location.pathname}${params.toString() ? `?${params.toString()}` : ""}`,
      { replace: true }
    );
  };

  const handleSortChange = (e) => {
    // Reset state for new filter/sort
    resetStateForNewFilters();
    setSort(e.target.value);
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      // Clear the state restoration timestamp after a longer period to prevent stale data
      const cleanup = () => {
        const lastRestoredTime = parseInt(
          localStorage.getItem(STATE_RESTORED_TIMESTAMP_KEY) || "0",
          10
        );
        const timeSinceRestoration = Date.now() - lastRestoredTime;

        // Only clear if it's been more than 5 minutes since restoration
        if (timeSinceRestoration > 5 * 60 * 1000) {
          localStorage.removeItem(STATE_RESTORED_TIMESTAMP_KEY);
        }
      };

      // Delay the cleanup to allow for quick remounts
      setTimeout(cleanup, 5000);
    };
  }, []);

  // Add a cleanup and state protection mechanism
  useEffect(() => {
    // This will run when the component is about to re-render with new dependencies
    // Check if we're going to do a fresh fetch that would reset state
    return () => {
      // If we have restored state and are about to trigger a fetch due to filter changes
      // make sure we save the current state first to prevent overwriting with empty state
      if (stateRestoredRef.current && vehicles.length > 0) {
        console.log("Saving state before potential filter change");
        savePageState();
      }
    };
  }, [
    sort,
    selectedTag,
    searchFromUrl,
    startsWithFromUrl,
    selectedMake,
    selectedModel,
  ]);

  return (
    <div>
      <div className="mb-6">
        <SearchBar compact />
        <AlphabetFilter
          startsWithFromUrl={startsWithFromUrl}
          handleLetterClick={handleLetterClick}
        />
      </div>

      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-6">
        <h1 className="text-2xl font-bold text-primary-800">
          {t("explore.title")}
        </h1>
        <div className="flex items-center gap-2 w-full sm:w-auto">
          <label className="text-sm text-gray-600 whitespace-nowrap">
            {t("explore.sort_by")}:
          </label>
          <select
            value={sort}
            onChange={handleSortChange}
            className="w-full sm:w-auto border border-gray-300 rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400"
          >
            <option value="recent">{t("explore.sort.recent")}</option>
            <option value="views">{t("explore.sort.most_views")}</option>
            <option value="likes">{t("explore.sort.most_likes")}</option>
            <option value="images">{t("explore.sort.most_images")}</option>
            <option value="discussions">
              {t("explore.sort.most_discussed")}
            </option>
            <option value="profiles">
              {t("explore.sort.richest_profiles")}
            </option>
            <option value="poorest_profiles">
              {t("explore.sort.poorest_profiles")}
            </option>
          </select>
        </div>
      </div>

      <TagsFilter
        selectedTag={selectedTag}
        handleTagSelect={handleTagSelect}
        showAllTags={showAllTags}
        setShowAllTags={setShowAllTags}
        savePageState={savePageState}
        t={t}
      />

      <VehicleFilter
        loadingMakes={loadingMakes}
        vehicleMakes={vehicleMakes}
        selectedMake={selectedMake}
        handleMakeSelect={handleMakeSelect}
        models={models}
        selectedModel={selectedModel}
        handleModelSelect={handleModelSelect}
        t={t}
      />

      {error && (
        <div className="bg-red-100 text-red-600 p-4 rounded-lg mb-6">
          {error}
        </div>
      )}

      <div
        ref={resultsRef}
        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6"
      >
        {vehicles.map((vehicle) => (
          <VehicleLink
            key={vehicle._id}
            vehicle={vehicle}
            variant="card"
            onClick={handleVehicleLinkClick}
          />
        ))}
      </div>

      {loading && (
        <div className="text-center py-8">
          <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-primary-400 border-r-transparent"></div>
        </div>
      )}

      {!loading && hasMore && (
        <div className="text-center mt-8">
          <button
            onClick={handleLoadMore}
            className="bg-primary-500 hover:bg-primary-600 text-white px-6 py-2 rounded-md shadow-md transition-all duration-200 hover:scale-105"
          >
            {t("explore.load_more")}
          </button>
        </div>
      )}

      {!loading && !hasMore && vehicles.length > 0 && (
        <p className="text-center mt-8 text-gray-600">
          {t("explore.end_of_list")}
        </p>
      )}

      {!loading && vehicles.length === 0 && (
        <div className="text-center py-12 bg-gray-50 rounded-lg dark:bg-gray-800 dark:text-gray-400">
          {selectedTag ? (
            <>
              <p className="text-gray-400">
                {t("explore.no_vehicles_with_tag", { tag: selectedTag })}
              </p>
              <p className="text-gray-400">
                {t("explore.tag_vehicles_prompt")}
              </p>
            </>
          ) : (
            <p className="text-gray-600 dark:text-gray-400">
              {t("explore.no_results")}
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default ExplorePage;
